import type {
  AdminUserDashboardReportsProps,
  LoadingStateProps,
  MessageStateProps,
  MetabaseGuestDashboardProps
} from './AdminUserDashboardReports.types'
import { useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { Spinner } from 'reactstrap'
import {
  type MetabaseResourceId,
  adminUserMetabaseCollectionDashboardsQueryOptions,
  adminUserMetabaseDashboardGuestEmbedQueryOptions,
  metabaseResourceIdPresent
} from './adminUserDashboard.queries'
import {
  EMPTY_DASHBOARDS,
  getDashboardId,
  getTokenRefreshDelay,
  loadMetabaseEmbedScript,
  normalizeMetabaseSiteUrl
} from './AdminUserDashboardReports.helpers'

const LoadingState = ({ message = 'Loading collection...' }: LoadingStateProps) => {
  return (
    <div className="text-center py-5">
      <Spinner color="primary" />
      <p className="mb-0 mt-3">{message}</p>
    </div>
  )
}

const MessageState = ({ title, message }: MessageStateProps) => {
  return (
    <div className="container mt-4">
      <h4>{title}</h4>
      <p className="mb-0">{message}</p>
    </div>
  )
}

const MetabaseGuestDashboard = ({ guestEmbed }: MetabaseGuestDashboardProps) => {
  const metabaseSiteUrl = normalizeMetabaseSiteUrl(guestEmbed.metabase_site_url)
  const embedScriptQuery = useQuery({
    queryKey: ['metabaseGuestEmbedScript', metabaseSiteUrl],
    queryFn: () => loadMetabaseEmbedScript(metabaseSiteUrl),
    enabled: metabaseSiteUrl !== null,
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
    refetchOnWindowFocus: false
  })

  if (embedScriptQuery.isError) {
    return <MessageState title="Dashboard Unavailable" message="Unable to load this dashboard." />
  }

  if (!embedScriptQuery.isSuccess) {
    return <LoadingState message="Preparing dashboard..." />
  }

  return (
    <metabase-dashboard
      key={guestEmbed.token}
      token={guestEmbed.token}
      with-title="true"
      with-downloads="false"
      style={{ display: 'block', height: '75vh', minHeight: 640 }}
    />
  )
}

const AdminUserDashboardReports = ({
  activeCollection,
  collectionId,
  metabaseCollectionsLoading,
  metabaseCollectionsError
}: AdminUserDashboardReportsProps) => {
  const activeCollectionId = activeCollection?.id ?? null
  const [activeDashboardId, setActiveDashboardId] = useState<MetabaseResourceId | null>(null)

  const dashboardsQuery = useQuery({
    ...adminUserMetabaseCollectionDashboardsQueryOptions(activeCollectionId),
    enabled: metabaseResourceIdPresent(activeCollectionId),
    select: dashboards => dashboards.filter(dashboard => metabaseResourceIdPresent(getDashboardId(dashboard)))
  })
  const dashboards = dashboardsQuery.data ?? EMPTY_DASHBOARDS
  const selectedDashboardId = useMemo(() => {
    if (metabaseResourceIdPresent(activeDashboardId)) {
      return activeDashboardId
    }

    return getDashboardId(dashboards[0])
  }, [activeDashboardId, dashboards])

  const guestEmbedQuery = useQuery({
    ...adminUserMetabaseDashboardGuestEmbedQueryOptions({
      collectionId: activeCollectionId,
      dashboardId: selectedDashboardId
    }),
    enabled: metabaseResourceIdPresent(activeCollectionId) && metabaseResourceIdPresent(selectedDashboardId),
    // React Query keeps a rendered dashboard alive while refreshing short-lived guest JWTs.
    refetchInterval: query => getTokenRefreshDelay(query.state.data?.expires_at) ?? false,
    refetchIntervalInBackground: true
  })

  const renderDashboardTabs = () => {
    if (dashboards.length <= 1) {
      return null
    }

    return (
      <div className="nav nav-pills mb-3 admin-user-reports-dashboard-tabs">
        {dashboards.map(dashboard => {
          const dashboardId = getDashboardId(dashboard)
          if (!metabaseResourceIdPresent(dashboardId)) return null

          const isActive = `${dashboardId}` === `${selectedDashboardId}`

          return (
            <button
              key={dashboardId}
              type="button"
              className={`nav-item nav-link admin-user-reports-dashboard-tab ${
                isActive ? 'active admin-user-reports-dashboard-tab--active' : ''
              }`}
              onClick={() => setActiveDashboardId(dashboardId)}
            >
              {dashboard.name || 'Untitled Dashboard'}
            </button>
          )
        })}
      </div>
    )
  }

  const renderReportsContent = () => {
    if (!metabaseResourceIdPresent(activeCollectionId)) {
      return <MessageState title="No Collection Selected" message="Select a collection to view dashboards." />
    }

    if (dashboardsQuery.isLoading) {
      return <LoadingState />
    }

    if (dashboardsQuery.isError) {
      return <MessageState title="Dashboards Unavailable" message="Unable to load dashboards for this collection." />
    }

    if (!metabaseResourceIdPresent(selectedDashboardId)) {
      return <MessageState title="No Dashboards Available" message="This collection has no dashboards." />
    }

    const guestEmbed = guestEmbedQuery.data ?? null

    return (
      <>
        {renderDashboardTabs()}
        {guestEmbedQuery.isPending ? <LoadingState message="Loading dashboard..." /> : null}
        {guestEmbedQuery.isError && !guestEmbed ? (
          <MessageState title="Dashboard Unavailable" message="Unable to load this dashboard right now." />
        ) : null}
        {guestEmbed ? <MetabaseGuestDashboard guestEmbed={guestEmbed} /> : null}
      </>
    )
  }

  if (metabaseCollectionsLoading) {
    return <LoadingState />
  }

  if (metabaseCollectionsError) {
    return <MessageState title="Collections Unavailable" message={metabaseCollectionsError} />
  }

  if (!metabaseResourceIdPresent(collectionId)) {
    return (
      <MessageState
        title="No Collections Assigned"
        message="Your client admin account does not currently have collection access."
      />
    )
  }

  return <div className="container-fluid px-0">{renderReportsContent()}</div>
}

export default AdminUserDashboardReports
